import { QueryClient, useQuery } from '@tanstack/react-query';

import {
  GetFixHoofdstuk200,
  HoofdstukSchema,
  HoofdstukSchemaObjectStatus,
} from '@/api';
import { useHoofdstuk } from '@/api/queries/objects';
import { GRONDSLAGEN, Grondslag } from '@/constants/grondslagen';

export type GrondslagInEditor = {
  grondslag: Grondslag;
  hoofdstukken: HoofdstukSchema[];
};

export const useHoofdstukkenInEditor = (besluitId: string) =>
  useQuery(getHoofdstukQueryOptions(besluitId));

export const ensureHoofdstukkenInEditorQueryData = async ({
  besluitId,
  queryClient,
}: {
  besluitId: string;
  queryClient: QueryClient;
}) => {
  const response = await queryClient.ensureQueryData(
    getHoofdstukQueryOptions(besluitId)
  );

  return selectHoofdstukkenInEditor(response);
};

export const fetchHoofdstukkenInEditorQueryData = async ({
  besluitId,
  queryClient,
}: {
  besluitId: string;
  queryClient: QueryClient;
}) => {
  const response = await queryClient.fetchQuery(
    getHoofdstukQueryOptions(besluitId)
  );

  return selectHoofdstukkenInEditor(response);
};

/**
 * Function that returns the queryOptions for fetching the hoofdstukken in the editor
 */
const getHoofdstukQueryOptions = (besluitId: string) => {
  return useHoofdstuk.getOptions(
    {
      page: 1,
      size: 100,
      sort: [{ field: 'Nummer', model: 'Hoofdstuk', direction: 'asc' }],
      filter: [
        {
          model: 'Besluit',
          field: 'ID',
          op: 'eq',
          value: besluitId,
        },
        {
          model: 'Hoofdstuk',
          field: 'ObjectStatus',
          op: 'ne',
          value: HoofdstukSchemaObjectStatus.IngetrokkenGepubliceerd,
        },
        { model: 'Hoofdstuk', field: 'Volgend', op: 'is_null', value: '' },
      ],
    },
    {
      select: selectHoofdstukkenInEditor,
    }
  );
};

/**
 * Returns an array of grondslagen including the hoofdstukken in the grondslag
 */
const selectHoofdstukkenInEditor = (data: GetFixHoofdstuk200) => {
  if (!data.objects?.length) return [];

  return GRONDSLAGEN.map((grondslag) => {
    if (!data.objects?.length) return;

    const hoofdstukkenInGrondslag = data.objects.filter(
      (hoofdstuk) => hoofdstuk.Grondslag === grondslag.id
    );

    if (!hoofdstukkenInGrondslag.length) return;

    return {
      grondslag,
      hoofdstukken: hoofdstukkenInGrondslag,
    };
  }).filter((grondslag): grondslag is GrondslagInEditor => Boolean(grondslag));
};
